import React, { useRef } from 'react';
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Button,
    useDisclosure,
    useToast,
    Box,
    VStack,
    HStack,
    Heading,
    Text,
    SimpleGrid,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Switch,
    FormControl,
    FormLabel,
    Badge,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../utils/init-firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
    SubmitButton,
    InputControl,
    TextareaControl,
    SelectControl,
    NumberInputControl,
} from 'formik-chakra-ui';
import { FaRegBuilding } from 'react-icons/fa';
import DividerWithText from '../DividerWithText';

// Class Valuation API Configuration
const CLASS_API_BASE_URL = process.env.REACT_APP_CLASS_API_URL || 'https://api.classvaluation.com/intg';
const CLASS_API_KEY = process.env.REACT_APP_CLASS_API_KEY;
const CLASS_ORG_ID = process.env.REACT_APP_CLASS_ORG_ID;
const CLASS_PRODUCT_ID = process.env.REACT_APP_CLASS_PRODUCT_ID;

// Validation Schema using Yup
const validationSchema = Yup.object({
    // Required fields from Class API
    productId: Yup.string().required('Product ID is required'),
    
    // Property Information (required)
    property: Yup.object({
        street: Yup.string().required('Street address is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required').max(2, 'State must be 2 characters'),
        zip: Yup.string().required('Zip code is required'),
        county: Yup.string(),
        line2: Yup.string(),
    }),
    
    // Loan Information (required)
    loanInfo: Yup.object({
        loanType: Yup.string().required('Loan type is required'),
        loanNumber: Yup.string(),
        loanAmount: Yup.number().positive('Must be positive').integer(),
        fhaNumber: Yup.string(),
        purchaseAmount: Yup.number().positive('Must be positive'),
    }),
    
    // Contacts (required - at least one)
    contacts: Yup.array().of(
        Yup.object({
            type: Yup.string().required('Contact type is required'),
            firstName: Yup.string().required('First name is required'),
            lastName: Yup.string().required('Last name is required'),
            primaryContact: Yup.boolean(),
            contactMethods: Yup.array().of(
                Yup.object({
                    type: Yup.string(),
                    value: Yup.string(),
                    primaryContact: Yup.boolean(),
                })
            ),
        })
    ).min(1, 'At least one contact is required'),
    
    // Transaction Purpose (required)
    purpose: Yup.string().required('Transaction purpose is required'),
    
    // Optional fields
    occupancy: Yup.string(),
    propertyTypeEnum: Yup.string(),
    instructions: Yup.string(),
    contractPrice: Yup.number().positive('Must be positive'),
    rushOrder: Yup.boolean(),
    referenceNumber: Yup.string(),
    dueDate: Yup.date().nullable(),
    estimatedClosingDate: Yup.date().nullable(),
});

export default function AppraisalOrderDrawer({ client, fullButton = false }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const btnRef = useRef();
    const { currentUser } = useAuth();
    const toast = useToast();

    // Extract property address from client data
    const getPropertyAddress = () => {
        const property = client['Loan Details']['Property Address'] || {};
        return {
            street: property['Street'] || '',
            line2: property['Unit'] || '',
            city: property['City'] || '',
            state: property['State'] || '',
            zip: property['Zip'] || '',
            county: property['County'] || '',
        };
    };

    // Initial form values sourced from the client document
    const initialValues = {
        productId: CLASS_PRODUCT_ID || '',
        referenceNumber: client['Loan Details']['Loan #'] || '',
        
        // Property Information
        property: getPropertyAddress(),
        
        // Loan Information
        loanInfo: {
            loanType: 'Conventional', // Default, user can change
            loanNumber: client['Loan Details']['Loan #'] || '',
            loanAmount: client['Loan Details']['Loan Amount'] || '',
            fhaNumber: '',
            purchaseAmount: client['Loan Details']['Purchase Price'] || '',
        },
        
        // Contacts - Pre-populate with borrower
        contacts: [
            {
                type: 'Borrower',
                firstName: client['Loan Details']['First Name'] || '',
                lastName: client['Loan Details']['Last Name'] || '',
                primaryContact: true,
                contactMethods: [
                    {
                        type: 'Email',
                        value: client['Loan Details']['Email'] || '',
                        primaryContact: true,
                    },
                    {
                        type: 'MobilePhone',
                        value: client['Loan Details']['Phone Number'] || '',
                        primaryContact: false,
                    },
                ],
            },
        ],
        
        // Transaction details
        purpose: client['Loan Details']['Purchase or Refi'] === 'Purchase' ? 'Purchase' : 'Refinance',
        occupancy: 'Owner',
        propertyTypeEnum: 'SingleFamily',
        
        // Additional fields
        instructions: '',
        contractPrice: client['Loan Details']['Purchase Price'] || '',
        rushOrder: false,
        dueDate: '',
        estimatedClosingDate: '',
        dateOfContract: '',
        borrowerIntentToProceedDate: '',
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            // Prepare the API request payload
            const orderPayload = {
                productId: values.productId,
                referenceNumber: values.referenceNumber,
                property: values.property,
                loanInfo: {
                    ...values.loanInfo,
                    loanAmount: values.loanInfo.loanAmount ? parseInt(values.loanInfo.loanAmount) : null,
                },
                contacts: values.contacts.map(contact => ({
                    type: contact.type,
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    primaryContact: contact.primaryContact,
                    contactMethods: contact.contactMethods.filter(cm => cm.value), // Only include if value exists
                })),
                purpose: values.purpose,
                occupancy: values.occupancy || undefined,
                propertyTypeEnum: values.propertyTypeEnum || undefined,
                instructions: values.instructions || undefined,
                contractPrice: values.contractPrice || undefined,
                rushOrder: values.rushOrder || false,
                dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : undefined,
                estimatedClosingDate: values.estimatedClosingDate ? new Date(values.estimatedClosingDate).toISOString() : undefined,
                dateOfContract: values.dateOfContract ? new Date(values.dateOfContract).toISOString() : undefined,
                borrowerIntentToProceedDate: values.borrowerIntentToProceedDate ? new Date(values.borrowerIntentToProceedDate).toISOString() : undefined,
            };

            // Call Class Valuation API
            const response = await fetch(`${CLASS_API_BASE_URL}/orders?OrgId=${CLASS_ORG_ID}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${CLASS_API_KEY}`,
                    'api-version': '1.0',
                },
                body: JSON.stringify(orderPayload),
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `API Error: ${response.status}`);
            }

            // Update Firestore with order information
            const clientDocRef = doc(db, 'Clients', client.id);
            await updateDoc(clientDocRef, {
                'Loan Details.Order Appraisal': 'Yes',
                'Loan Details.Appraisal Order ID': responseData.orderId || responseData.id,
                'Loan Details.Appraisal Order Status': responseData.status || 'Pending',
                'Loan Details.Appraisal Order Reference': values.referenceNumber,
                'Date Appraisal Ordered': serverTimestamp(),
                'Last Edited By': {
                    Email: currentUser.email,
                    Name: currentUser.displayName,
                    photoURL: currentUser.photoURL,
                    uid: currentUser.uid,
                },
            });

            toast({
                title: 'Appraisal Order Submitted Successfully',
                description: `Order ID: ${responseData.orderId || responseData.id}. The appraisal has been ordered for ${values.property.street}.`,
                status: 'success',
                duration: 7000,
                isClosable: true,
                position: 'top',
            });
            
            onClose();
        } catch (error) {
            console.error("Error ordering appraisal:", error);
            toast({
                title: 'Error Submitting Appraisal Order',
                description: error.message || 'There was an error ordering the appraisal. Please check your configuration and try again.',
                status: 'error',
                duration: 7000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                ref={btnRef}
                onClick={onOpen}
                leftIcon={<FaRegBuilding />}
                colorScheme='teal'
                variant='solid'
                size={fullButton ? 'md' : 'sm'}
                width={fullButton ? '100%' : 'auto'}
                isDisabled={client['Loan Details']['Order Appraisal'] === 'Yes'}
            >
                {client['Loan Details']['Order Appraisal'] === 'Yes' ? 'Appraisal Ordered' : 'Order Appraisal'}
            </Button>

            <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={btnRef} size="xl">
                <DrawerOverlay />
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ handleSubmit, values, isSubmitting, setFieldValue }) => (
                        <Box as="form" onSubmit={handleSubmit}>
                            <DrawerContent>
                                <DrawerCloseButton />
                                <DrawerHeader borderBottomWidth="1px">
                                    <VStack align="start" spacing={1}>
                                        <HStack>
                                            <FaRegBuilding />
                                            <Heading size="md">Order Appraisal</Heading>
                                        </HStack>
                                        <Text fontSize="sm" fontWeight="normal" color="gray.600">
                                            {client['Loan Details']['First Name']} {client['Loan Details']['Last Name']} • Loan #{values.referenceNumber}
                                        </Text>
                                    </VStack>
                                </DrawerHeader>
                                
                                <DrawerBody>
                                    <Tabs colorScheme="teal" variant="enclosed">
                                        <TabList>
                                            <Tab>Property & Loan</Tab>
                                            <Tab>Contacts</Tab>
                                            <Tab>Additional Details</Tab>
                                        </TabList>

                                        <TabPanels>
                                            {/* Tab 1: Property & Loan Information */}
                                            <TabPanel>
                                                <VStack spacing={6} align="stretch">
                                                    {/* Property Information */}
                                                    <Box>
                                                        <DividerWithText text="Property Information" />
                                                        <VStack spacing={4} mt={4}>
                                                            <InputControl 
                                                                name="property.street" 
                                                                label="Street Address *"
                                                                inputProps={{ placeholder: '123 Main St' }}
                                                            />
                                                            <InputControl 
                                                                name="property.line2" 
                                                                label="Unit / Apt #"
                                                                inputProps={{ placeholder: 'Unit 4B' }}
                                                            />
                                                            <SimpleGrid columns={2} spacing={4}>
                                                                <InputControl 
                                                                    name="property.city" 
                                                                    label="City *"
                                                                    inputProps={{ placeholder: 'San Diego' }}
                                                                />
                                                                <InputControl 
                                                                    name="property.state" 
                                                                    label="State *"
                                                                    inputProps={{ placeholder: 'CA', maxLength: 2 }}
                                                                />
                                                            </SimpleGrid>
                                                            <SimpleGrid columns={2} spacing={4}>
                                                                <InputControl 
                                                                    name="property.zip" 
                                                                    label="Zip Code *"
                                                                    inputProps={{ placeholder: '92101' }}
                                                                />
                                                                <InputControl 
                                                                    name="property.county" 
                                                                    label="County"
                                                                    inputProps={{ placeholder: 'San Diego' }}
                                                                />
                                                            </SimpleGrid>
                                                            <SelectControl
                                                                name="propertyTypeEnum"
                                                                label="Property Type"
                                                                selectProps={{ placeholder: 'Select property type' }}
                                                            >
                                                                <option value="SingleFamily">Single Family</option>
                                                                <option value="Condominium">Condominium</option>
                                                                <option value="TownhouseorRowhouse">Townhouse</option>
                                                                <option value="TwoToFourFamily">2-4 Unit</option>
                                                                <option value="MultiFamily">Multi Family</option>
                                                                <option value="ManufacturedHome">Manufactured Home</option>
                                                                <option value="Cooperative">Cooperative</option>
                                                                <option value="VacantLand">Vacant Land</option>
                                                                <option value="Other">Other</option>
                                                            </SelectControl>
                                                            <SelectControl
                                                                name="occupancy"
                                                                label="Occupancy Type"
                                                                selectProps={{ placeholder: 'Select occupancy' }}
                                                            >
                                                                <option value="Owner">Owner Occupied</option>
                                                                <option value="Secondary">Second Home</option>
                                                                <option value="Investor">Investment Property</option>
                                                                <option value="Tenant">Tenant Occupied</option>
                                                                <option value="Vacant">Vacant</option>
                                                            </SelectControl>
                                                        </VStack>
                                                    </Box>

                                                    {/* Loan Information */}
                                                    <Box>
                                                        <DividerWithText text="Loan Information" />
                                                        <VStack spacing={4} mt={4}>
                                                            <SimpleGrid columns={2} spacing={4}>
                                                                <InputControl 
                                                                    name="loanInfo.loanNumber" 
                                                                    label="Loan Number"
                                                                    inputProps={{ placeholder: 'Loan #' }}
                                                                />
                                                                <SelectControl
                                                                    name="loanInfo.loanType"
                                                                    label="Loan Type *"
                                                                    selectProps={{ placeholder: 'Select loan type' }}
                                                                >
                                                                    <option value="Conventional">Conventional</option>
                                                                    <option value="FHA">FHA</option>
                                                                    <option value="VA">VA</option>
                                                                    <option value="USDA">USDA</option>
                                                                    <option value="NewConstruction">New Construction</option>
                                                                    <option value="Other">Other</option>
                                                                </SelectControl>
                                                            </SimpleGrid>
                                                            <SimpleGrid columns={2} spacing={4}>
                                                                <NumberInputControl 
                                                                    name="loanInfo.loanAmount" 
                                                                    label="Loan Amount"
                                                                    numberInputProps={{ 
                                                                        precision: 0,
                                                                        placeholder: '$000,000'
                                                                    }}
                                                                />
                                                                <NumberInputControl 
                                                                    name="loanInfo.purchaseAmount" 
                                                                    label="Purchase Amount"
                                                                    numberInputProps={{ 
                                                                        precision: 0,
                                                                        placeholder: '$000,000'
                                                                    }}
                                                                />
                                                            </SimpleGrid>
                                                            {values.loanInfo.loanType === 'FHA' && (
                                                                <InputControl 
                                                                    name="loanInfo.fhaNumber" 
                                                                    label="FHA Case Number"
                                                                    inputProps={{ placeholder: '###-####### OR ###-#######-###' }}
                                                                />
                                                            )}
                                                            <SelectControl
                                                                name="purpose"
                                                                label="Transaction Purpose *"
                                                                selectProps={{ placeholder: 'Select purpose' }}
                                                            >
                                                                <option value="Purchase">Purchase</option>
                                                                <option value="Refinance">Refinance</option>
                                                                <option value="Construction">Construction</option>
                                                                <option value="HomeEquity">Home Equity</option>
                                                                <option value="AscertainMarketValue">Market Value</option>
                                                                <option value="Other">Other</option>
                                                            </SelectControl>
                                                        </VStack>
                                                    </Box>
                                                </VStack>
                                            </TabPanel>

                                            {/* Tab 2: Contacts */}
                                            <TabPanel>
                                                <VStack spacing={4} align="stretch">
                                                    <DividerWithText text="Contact Information" />
                                                    <FieldArray name="contacts">
                                                        {({ push, remove }) => (
                                                            <>
                                                                {values.contacts.map((contact, index) => (
                                                                    <Box 
                                                                        key={index} 
                                                                        p={4} 
                                                                        borderWidth="1px" 
                                                                        borderRadius="md"
                                                                        position="relative"
                                                                    >
                                                                        <VStack spacing={4} align="stretch">
                                                                            <HStack justify="space-between">
                                                                                <Badge colorScheme="teal">Contact {index + 1}</Badge>
                                                                                {values.contacts.length > 1 && (
                                                                                    <Button 
                                                                                        size="xs" 
                                                                                        colorScheme="red" 
                                                                                        variant="ghost"
                                                                                        onClick={() => remove(index)}
                                                                                    >
                                                                                        Remove
                                                                                    </Button>
                                                                                )}
                                                                            </HStack>
                                                                            
                                                                            <SelectControl
                                                                                name={`contacts.${index}.type`}
                                                                                label="Contact Type *"
                                                                                selectProps={{ placeholder: 'Select type' }}
                                                                            >
                                                                                <option value="Borrower">Borrower</option>
                                                                                <option value="Coborrower">Co-Borrower</option>
                                                                                <option value="ListingAgent">Listing Agent</option>
                                                                                <option value="BuyingAgent">Buyer's Agent</option>
                                                                                <option value="Seller">Seller</option>
                                                                                <option value="PropertyAccess">Property Access</option>
                                                                                <option value="Attorney">Attorney</option>
                                                                                <option value="Other">Other</option>
                                                                            </SelectControl>
                                                                            
                                                                            <SimpleGrid columns={2} spacing={4}>
                                                                                <InputControl 
                                                                                    name={`contacts.${index}.firstName`} 
                                                                                    label="First Name *"
                                                                                />
                                                                                <InputControl 
                                                                                    name={`contacts.${index}.lastName`} 
                                                                                    label="Last Name *"
                                                                                />
                                                                            </SimpleGrid>

                                                                            <FormControl>
                                                                                <FormLabel>Primary Contact</FormLabel>
                                                                                <Switch
                                                                                    isChecked={contact.primaryContact}
                                                                                    onChange={(e) => setFieldValue(`contacts.${index}.primaryContact`, e.target.checked)}
                                                                                />
                                                                            </FormControl>

                                                                            <DividerWithText text="Contact Methods" />
                                                                            
                                                                            {contact.contactMethods?.map((method, methodIndex) => (
                                                                                <HStack key={methodIndex} spacing={2}>
                                                                                    <SelectControl
                                                                                        name={`contacts.${index}.contactMethods.${methodIndex}.type`}
                                                                                        label={methodIndex === 0 ? "Type" : ""}
                                                                                        selectProps={{ size: 'sm' }}
                                                                                    >
                                                                                        <option value="Email">Email</option>
                                                                                        <option value="MobilePhone">Mobile Phone</option>
                                                                                        <option value="WorkPhone">Work Phone</option>
                                                                                        <option value="HomePhone">Home Phone</option>
                                                                                    </SelectControl>
                                                                                    <InputControl 
                                                                                        name={`contacts.${index}.contactMethods.${methodIndex}.value`}
                                                                                        label={methodIndex === 0 ? "Contact Info" : ""}
                                                                                        inputProps={{ 
                                                                                            size: 'sm',
                                                                                            placeholder: method.type === 'Email' ? 'email@example.com' : '(555) 555-5555'
                                                                                        }}
                                                                                    />
                                                                                </HStack>
                                                                            ))}
                                                                        </VStack>
                                                                    </Box>
                                                                ))}
                                                                
                                                                <Button
                                                                    onClick={() => push({
                                                                        type: '',
                                                                        firstName: '',
                                                                        lastName: '',
                                                                        primaryContact: false,
                                                                        contactMethods: [
                                                                            { type: 'Email', value: '', primaryContact: true },
                                                                            { type: 'MobilePhone', value: '', primaryContact: false },
                                                                        ],
                                                                    })}
                                                                    colorScheme="teal"
                                                                    variant="outline"
                                                                    size="sm"
                                                                >
                                                                    + Add Another Contact
                                                                </Button>
                                                            </>
                                                        )}
                                                    </FieldArray>
                                                </VStack>
                                            </TabPanel>

                                            {/* Tab 3: Additional Details */}
                                            <TabPanel>
                                                <VStack spacing={6} align="stretch">
                                                    <Box>
                                                        <DividerWithText text="Order Details" />
                                                        <VStack spacing={4} mt={4}>
                                                            <InputControl 
                                                                name="referenceNumber" 
                                                                label="Reference Number"
                                                                inputProps={{ placeholder: 'Your internal reference' }}
                                                            />
                                                            <InputControl 
                                                                name="productId" 
                                                                label="Product ID *"
                                                                inputProps={{ placeholder: 'Class Valuation Product ID' }}
                                                            />
                                                            <SimpleGrid columns={2} spacing={4}>
                                                                <NumberInputControl 
                                                                    name="contractPrice" 
                                                                    label="Contract Price"
                                                                    numberInputProps={{ 
                                                                        precision: 0,
                                                                        placeholder: '$000,000'
                                                                    }}
                                                                />
                                                                <FormControl>
                                                                    <FormLabel>Rush Order</FormLabel>
                                                                    <Switch
                                                                        isChecked={values.rushOrder}
                                                                        onChange={(e) => setFieldValue('rushOrder', e.target.checked)}
                                                                    />
                                                                </FormControl>
                                                            </SimpleGrid>
                                                        </VStack>
                                                    </Box>

                                                    <Box>
                                                        <DividerWithText text="Important Dates" />
                                                        <VStack spacing={4} mt={4}>
                                                            <InputControl 
                                                                name="dueDate" 
                                                                label="Due Date"
                                                                inputProps={{ type: 'date' }}
                                                            />
                                                            <InputControl 
                                                                name="estimatedClosingDate" 
                                                                label="Estimated Closing Date"
                                                                inputProps={{ type: 'date' }}
                                                            />
                                                            <InputControl 
                                                                name="dateOfContract" 
                                                                label="Date of Contract"
                                                                inputProps={{ type: 'date' }}
                                                            />
                                                            <InputControl 
                                                                name="borrowerIntentToProceedDate" 
                                                                label="Borrower Intent to Proceed Date"
                                                                inputProps={{ type: 'date' }}
                                                            />
                                                        </VStack>
                                                    </Box>

                                                    <Box>
                                                        <DividerWithText text="Special Instructions" />
                                                        <TextareaControl
                                                            name="instructions"
                                                            label="Instructions / Notes"
                                                            textareaProps={{
                                                                placeholder: 'Any special instructions or requirements for this appraisal order...',
                                                                rows: 6,
                                                            }}
                                                        />
                                                    </Box>
                                                </VStack>
                                            </TabPanel>
                                        </TabPanels>
                                    </Tabs>
                                </DrawerBody>
                                
                                <DrawerFooter borderTopWidth="1px">
                                    <HStack spacing={3} width="100%" justify="space-between">
                                        <Text fontSize="xs" color="gray.500">
                                            * Required fields
                                        </Text>
                                        <HStack>
                                            <Button variant="outline" onClick={onClose}>
                                                Cancel
                                            </Button>
                                            <SubmitButton 
                                                colorScheme="teal" 
                                                isLoading={isSubmitting}
                                                loadingText="Submitting Order..."
                                            >
                                                Submit Appraisal Order
                                            </SubmitButton>
                                        </HStack>
                                    </HStack>
                                </DrawerFooter>
                            </DrawerContent>
                        </Box>
                    )}
                </Formik>
            </Drawer>
        </>
    );
}